import { Link, useLocation } from "wouter";
import { signOut, useSession } from "../auth/use-session.js";
import { useMe } from "../api/use-me.js";
import { RoleBadge } from "./role-badge.js";
import { SearchBar } from "./search-bar.js";
import { ViewScopeBanner, ViewScopeSwitcher } from "./view-scope-switcher.js";

// 1.D.2 Paanavigaatio. Linkit aktiivinen-tilan korostuksella.
//
// Aktiivisen tunnistuksen pidamme erikseen pienena puhtaana funktiona
// (`isActive`) jotta sen voi unit-testata ilman DOM:ia. Kayttaa exact
// matchia juurelle "/" ja prefix-matchia muille (esim. "/selaa/123"
// korostaa "/selaa"-linkin).

export type NavLink = {
  href: string;
  label: string;
};

export const NAV_LINKS: NavLink[] = [
  { href: "/todo", label: "TODO" },
  { href: "/selaa", label: "Selaa" },
  { href: "/projektit", label: "Projektit" },
  { href: "/levyt", label: "Levyt" },
  { href: "/historia", label: "Historia" },
  { href: "/asetukset", label: "Asetukset" },
  { href: "/lataa", label: "Lataa" },
];

export function isActive(currentPath: string, linkHref: string): boolean {
  if (linkHref === "/") return currentPath === "/";
  return currentPath === linkHref || currentPath.startsWith(linkHref + "/");
}

export function Nav() {
  const [location, navigate] = useLocation();
  const session = useSession();
  const { me } = useMe();
  const roles = me?.user.roles ?? [];
  const isAdmin = roles.includes("admin");
  return (
    <>
      <nav className="border-b border-slate-800 bg-slate-950/80 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center gap-6 px-6 py-4">
          <Link href="/" className="text-lg font-semibold tracking-tight text-slate-50">
            kratti
          </Link>
          <ul className="flex gap-1 text-sm">
            {NAV_LINKS.map((link) => {
              const active = isActive(location, link.href);
              const className = active
                ? "rounded-md bg-slate-800 px-3 py-1.5 text-slate-100"
                : "rounded-md px-3 py-1.5 text-slate-400 hover:bg-slate-900 hover:text-slate-200";
              return (
                <li key={link.href}>
                  <Link href={link.href} className={className}>
                    {link.label}
                  </Link>
                </li>
              );
            })}
            {isAdmin && (
              <li>
                <Link
                  href="/kayttajat"
                  className={
                    isActive(location, "/kayttajat")
                      ? "rounded-md bg-slate-800 px-3 py-1.5 text-slate-100"
                      : "rounded-md px-3 py-1.5 text-slate-400 hover:bg-slate-900 hover:text-slate-200"
                  }
                >
                  Käyttäjät
                </Link>
              </li>
            )}
          </ul>
          {session.isLoggedIn && (
            <div className="ml-2 hidden md:block">
              <SearchBar />
            </div>
          )}
          <div className="ml-auto flex items-center gap-3 text-sm">
            {session.isPending ? (
              <span className="text-slate-500">…</span>
            ) : session.isLoggedIn ? (
              <>
                <span className="text-slate-300" data-testid="nav-user-name">
                  {session.user!.name}
                </span>
                {roles.map((r) => (
                  <RoleBadge key={r} role={r} />
                ))}
                {isAdmin && <ViewScopeSwitcher />}
                <button
                  type="button"
                  onClick={async () => {
                    await signOut();
                    navigate("/login");
                  }}
                  className="rounded-md border border-slate-700 px-3 py-1.5 text-slate-300 hover:bg-slate-900"
                >
                  Kirjaudu ulos
                </button>
              </>
            ) : (
              <Link
                href="/login"
                className="rounded-md border border-slate-700 px-3 py-1.5 text-slate-300 hover:bg-slate-900"
              >
                Kirjaudu sisaan
              </Link>
            )}
          </div>
        </div>
      </nav>
      <ViewScopeBanner />
    </>
  );
}
